import React, { useEffect, useMemo, useState } from "react";
import { EntityTablePage, DeleteConfirmationConfig } from "@/components/EntityTablePage";
import { getEstimateTableConfig } from "@/config/tableConfigs/estimateTableConfig";
import { MarketOption } from "@/config/tableConfigs/projectTableConfig";
import { estimateService } from "@/services/estimateService";
import { EstimateType } from "@/types/db/estimates";
import useAuth from "@/hooks/useAuth";
import { supabase } from "@/lib/supabase-client";
import { FromDB } from "@/types";
import { NoPermissionScreen } from "@/components/screens/shared/NoPermissionScreen";
import { DeleteEstimateModal } from "@/components/Modals/DeleteEstimateModal";
import AsyncStorage from "@react-native-async-storage/async-storage";
import { useSetPageTitle } from "@/hooks/useSetPageTitle";

export default function EstimatesPage() {
  const { userRoles, displayNames } = useAuth();
  const [marketOptions, setMarketOptions] = useState<MarketOption[]>([]);
  useSetPageTitle(displayNames?.estimates?.plural);

  // Fetch markets on mount
  useEffect(() => {
    (async () => {
      const { data } = await supabase
        .schema("lists")
        .from("markets")
        .select("id, name")
        .order("name", { ascending: true });

      if (data) {
        setMarketOptions(data.map((m) => ({ value: m.id, label: m.name })));
      }
    })();
  }, []);

  const tableConfig = useMemo(
    () => getEstimateTableConfig(userRoles, marketOptions, displayNames),
    [userRoles, marketOptions, displayNames],
  );

  // Check if user has estimates read permission
  const hasEstimatesAccess = useMemo(() => {
    return userRoles?.some((r) => r.permissions?.estimates?.read === true) ?? false;
  }, [userRoles]);

  const deleteConfirmation: DeleteConfirmationConfig<FromDB<EstimateType>> = useMemo(
    () => ({
      storageKey: "estimate_delete_skip_confirmation",
      renderModal: ({ count, onConfirm, onCancel }) => (
        <DeleteEstimateModal
          count={count}
          onConfirm={async (dontShowAgain) => {
            if (dontShowAgain) {
              await AsyncStorage.setItem("estimate_delete_skip_confirmation", "true");
            }
            onConfirm();
          }}
          onCancel={onCancel}
        />
      ),
    }),
    [],
  );

  if (!hasEstimatesAccess) {
    return <NoPermissionScreen entityName={displayNames?.estimates?.plural || "Estimates"} />;
  }

  return (
    <EntityTablePage<FromDB<EstimateType>>
      {...tableConfig}
      fetchData={estimateService.getEstimates}
      updateEntities={estimateService.saveRowPatches}
      deleteEntities={estimateService.deleteEstimatesByIds}
      deleteConfirmation={deleteConfirmation}
    />
  );
}
